import { useEffect, useState, type SyntheticEvent } from "react";
import { ChevronLeft, ChevronRight, Minus, Plus, Truck } from "lucide-react";
import type { ProductImage, ProductLayout } from "../../../lib/api";
import { formatMoney } from "../../../lib/money";
import { useShop } from "../../../stores/cart";
import { Price, ProductCard, WishlistButton, isSoldOut } from "./ProductCard";
import { useStorefrontContext } from "./StorefrontContext";
import { useCarousel } from "./useCarousel";

/** With auto-advance on, the product page steps to the next photo after this long, until the shopper takes over. */
const GALLERY_AUTOPLAY_MS = 5000;

/** Until a photo loads, its frame uses the store's classic 4:5 shape. */
const DEFAULT_RATIO = 4 / 5;

/**
 * The product's photos. The frame takes the shape of the photo on show, so
 * landscape and portrait photos are never cropped. With several photos there
 * are arrows, swiping, thumbnails, a counter, and autoplay if the store has it on.
 */
function ProductGallery({
  images,
  name,
  layout,
  autoAdvance,
}: {
  images: ProductImage[];
  name: string;
  layout: ProductLayout;
  /** The store's "Auto-advance photos" setting. */
  autoAdvance: boolean;
}) {
  const [ratios, setRatios] = useState<Record<string, number>>({});
  const [paused, setPaused] = useState(false);
  const carousel = useCarousel(images.length, { autoplayMs: autoAdvance ? GALLERY_AUTOPLAY_MS : 0, paused });
  const many = images.length > 1;

  const measure = (url: string) => (e: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (w > 0 && h > 0) setRatios((prev) => (prev[url] === w / h ? prev : { ...prev, [url]: w / h }));
  };

  if (layout === "gallery") {
    // Gallery layout: every photo stacked at its own shape, details stay in view.
    return (
      <div className="zs-gallery">
        {images.map((img, i) => (
          <div key={`${img.url}-${i}`} className="zs-gallery__main zs-gallery__main--natural">
            <img src={img.url} alt={img.alt || `${name} photo ${i + 1}`} />
          </div>
        ))}
      </div>
    );
  }

  const current = images[carousel.index];
  return (
    <div
      className="zs-gallery"
      role="region"
      aria-roledescription="carousel"
      aria-label={`${name} photos`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") carousel.prev();
        if (e.key === "ArrowRight") carousel.next();
      }}
    >
      <div
        className="zs-gallery__main"
        style={{ aspectRatio: String(current ? (ratios[current.url] ?? DEFAULT_RATIO) : DEFAULT_RATIO) }}
        {...(many ? carousel.swipeHandlers : {})}
      >
        {images.map((img, i) => (
          <div
            key={`${img.url}-${i}`}
            className="zs-photo"
            data-active={i === carousel.index ? "true" : undefined}
            aria-hidden={i === carousel.index ? undefined : true}
          >
            <img className="zs-photo__backdrop" src={img.url} alt="" />
            <img
              className="zs-photo__img"
              src={img.url}
              alt={i === carousel.index ? img.alt || `${name} photo ${i + 1}` : ""}
              onLoad={measure(img.url)}
              draggable={false}
            />
          </div>
        ))}
        {many ? (
          <>
            <button type="button" className="zs-gallery__nav zs-gallery__nav--prev" aria-label="Previous photo" onClick={carousel.prev}>
              <ChevronLeft size={20} aria-hidden="true" />
            </button>
            <button type="button" className="zs-gallery__nav zs-gallery__nav--next" aria-label="Next photo" onClick={carousel.next}>
              <ChevronRight size={20} aria-hidden="true" />
            </button>
            <span className="zs-gallery__count" aria-live="polite">
              {carousel.index + 1} / {images.length}
            </span>
          </>
        ) : null}
      </div>
      {many ? (
        <div className="zs-gallery__thumbs" role="group" aria-label="Product photos">
          {images.map((img, i) => (
            <button
              key={`${img.url}-${i}`}
              type="button"
              className="zs-thumb"
              aria-pressed={i === carousel.index}
              aria-label={`Show photo ${i + 1}`}
              onClick={() => carousel.show(i)}
            >
              <img src={img.url} alt="" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ProductPage({ productSlug }: { productSlug: string }) {
  const { store, slug, paths, go, findProduct } = useStorefrontContext();
  const product = findProduct(productSlug);
  const add = useShop((s) => s.add);
  const openCart = useShop((s) => s.setDrawerOpen);

  const [selected, setSelected] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelected({});
    setQuantity(1);
    setError(null);
    if (product) document.title = `${product.name} · ${store.name}`;
  }, [productSlug, product, store.name]);

  if (!product) {
    return (
      <section className="zs-page zs-empty">
        <h1 className="zs-title">Product not found</h1>
        <p>It may have sold out or been removed.</p>
        <button type="button" className="zs-btn" onClick={() => go(paths.shop)}>
          Back to the shop
        </button>
      </section>
    );
  }

  const soldOut = isSoldOut(product);
  const maxQuantity = Math.min(99, product.stock ?? 99);
  const related = store.products
    .filter((p) => p.id !== product.id && p.collection === product.collection)
    .slice(0, 4);
  const freeOver = store.deliveryOptions.find((d) => !d.pickup && d.freeOverMinor)?.freeOverMinor;

  const addToCart = () => {
    const missing = product.options.find((opt) => !selected[opt.name]);
    if (missing) {
      setError(`Choose a ${missing.name.toLowerCase()}`);
      return;
    }
    add(slug, {
      productId: product.id,
      slug: product.slug,
      name: product.name,
      image: product.images[0]?.url,
      priceMinor: product.priceMinor,
      options: selected,
      quantity,
    });
    openCart(true);
  };

  return (
    <section className="zs-page">
      <button type="button" className="zs-back" onClick={() => go(paths.shop)}>
        <ChevronLeft size={16} aria-hidden="true" />
        Shop{product.collection ? ` / ${product.collection}` : ""}
      </button>

      <div className={`zs-product zs-product--${store.productLayout}`}>
        <ProductGallery
          key={product.id}
          images={product.images}
          name={product.name}
          layout={store.productLayout}
          autoAdvance={store.autoAdvancePhotos ?? true}
        />

        <div className="zs-product__info">
          {product.collection ? <span className="zs-eyebrow">{product.collection}</span> : null}
          <h1 className="zs-title">{product.name}</h1>
          <Price product={product} large />
          {product.stock !== null && product.stock > 0 && product.stock <= 5 ? (
            <p className="zs-stock-note">Only {product.stock} left</p>
          ) : null}

          {product.options.map((opt) => (
            <fieldset key={opt.name} className="zs-option">
              <legend>
                {opt.name}
                {selected[opt.name] ? <span className="zs-option__value">{selected[opt.name]}</span> : null}
              </legend>
              <div className="zs-option__values">
                {opt.values.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className="zs-pill"
                    aria-pressed={selected[opt.name] === value}
                    onClick={() => {
                      setError(null);
                      setSelected((prev) => ({ ...prev, [opt.name]: value }));
                    }}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}

          <div className="zs-buy">
            <div className="zs-qty" aria-label="Quantity">
              <button
                type="button"
                aria-label="Decrease quantity"
                disabled={quantity <= 1}
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              >
                <Minus size={14} aria-hidden="true" />
              </button>
              <span aria-live="polite">{quantity}</span>
              <button
                type="button"
                aria-label="Increase quantity"
                disabled={quantity >= maxQuantity}
                onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
              >
                <Plus size={14} aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="zs-btn zs-btn--block"
              disabled={soldOut}
              onClick={addToCart}
            >
              {soldOut ? "Sold out" : "Add to cart"}
            </button>
          </div>
          {error ? (
            <p className="zs-error" role="alert">
              {error}
            </p>
          ) : null}

          {freeOver ? (
            <p className="zs-perk">
              <Truck size={16} aria-hidden="true" />
              Free delivery on orders over {formatMoney(freeOver, store.currency)}
            </p>
          ) : null}

          {product.description ? <p className="zs-description">{product.description}</p> : null}
        </div>
      </div>

      {related.length > 0 ? (
        <div className="zs-related">
          <h2 className="zs-subtitle">You may also like</h2>
          <div className="zs-grid">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
